import { Button, Offcanvas } from "react-bootstrap";
import { FiMenu } from "react-icons/fi";
import {
  TableColumnDef,
  configsEqual,
} from "../../utils/tableColumns";
import { TableColumnsController } from "../../hooks/useTableColumns";

type Props<T> = {
  columns: TableColumnDef<T>[];
  controller: TableColumnsController;
};

export function EditColumnsPanel<T>(props: Props<T>) {
  const c = props.controller;

  const draftVisibleCount = c.draft
    ? c.draft.order.filter((k) => {
        const col = props.columns.find((x) => x.key === k);
        if (!col) return false;
        if (col.contextGate && !col.contextGate()) return false;
        return !c.draft!.hidden.includes(k);
      }).length
    : 0;

  const draftIsDirty = c.draft ? !configsEqual(c.draft, c.config) : false;

  return (
    <Offcanvas
      show={c.showPanel}
      onHide={() => c.setShowPanel(false)}
      placement="end"
      style={{ width: "min(360px, 92vw)" }}
    >
      <Offcanvas.Header closeButton className="border-bottom">
        <Offcanvas.Title className="font-16">Edit Columns</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body
        className="p-3 d-flex flex-column"
        style={{ overflow: "hidden" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span
            className="text-uppercase text-muted"
            style={{ fontSize: 11, letterSpacing: 0.5 }}
          >
            Drag to reorder · toggle to hide
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none"
            onClick={c.resetDraft}
          >
            Reset
          </button>
        </div>

        <ul
          className="list-unstyled mb-0 flex-grow-1"
          style={{ overflowY: "auto" }}
        >
          {(c.draft?.order ?? []).map((key) => {
            const col = props.columns.find((x) => x.key === key);
            if (!col) return null;
            if (col.contextGate && !col.contextGate()) return null;

            const hidden = c.isDraftColHidden(key);
            const isDragging = c.draggingKey === key;
            return (
              <li
                key={key}
                draggable
                onDragStart={() => c.setDraggingKey(key)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (c.draggingKey) c.reorderDraft(c.draggingKey, key);
                  c.setDraggingKey(null);
                }}
                onDragEnd={() => c.setDraggingKey(null)}
                className="d-flex align-items-center gap-2 border rounded mb-1 px-2 py-1"
                style={{
                  cursor: "grab",
                  background: isDragging ? "#eef2ff" : "#fff",
                  opacity: isDragging ? 0.6 : 1,
                  userSelect: "none",
                }}
              >
                <FiMenu className="text-muted flex-shrink-0" size={14} />
                <label
                  className="d-flex align-items-center gap-2 flex-grow-1 mb-0"
                  style={{ cursor: "pointer", fontSize: 13 }}
                >
                  <input
                    type="checkbox"
                    className="form-check-input mt-0"
                    checked={!hidden}
                    onChange={() => c.toggleDraftColHidden(key)}
                  />
                  <span
                    className={hidden ? "text-muted" : ""}
                    style={{
                      textDecoration: hidden ? "line-through" : "none",
                    }}
                  >
                    {col.label}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </Offcanvas.Body>

      <div
        className="border-top d-flex justify-content-between align-items-center gap-2"
        style={{ padding: "12px 16px", background: "#fff" }}
      >
        <span
          className={draftVisibleCount === 0 ? "text-danger" : "text-muted"}
          style={{ fontSize: 11 }}
        >
          {draftVisibleCount === 0
            ? "Keep at least one column visible."
            : `${draftVisibleCount} column${
                draftVisibleCount === 1 ? "" : "s"
              } visible`}
        </span>
        <div className="d-flex gap-2">
          <Button
            variant="light"
            onClick={() => c.setShowPanel(false)}
            style={{ fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-wmx"
            onClick={c.applyDraft}
            disabled={!draftIsDirty || draftVisibleCount === 0}
          >
            Apply
          </Button>
        </div>
      </div>
    </Offcanvas>
  );
}

export default EditColumnsPanel;
